import { applyPatches, enablePatches, produce, produceWithPatches, type Patch } from 'immer';
import { create } from 'zustand';
import type { ProjectDoc, ZoneNumber } from '../model/types';

enablePatches();

export type SelectionKind = 'source' | 'area' | 'equipment' | 'substance' | 'object' | 'underlay';
export type Selection = { kind: SelectionKind; id: string } | null;
export type DockTab = 'schedule' | 'checks' | 'summation' | 'equipment' | 'substances';
export type ViewMode = 'persp' | 'plan' | 'elev';
export type SaveState = 'saved' | 'saving' | 'error';

interface HistoryEntry {
  label: string;
  patches: Patch[];
  inverse: Patch[];
}

interface State {
  projectId: string | null;
  /** Where the open project is saved: this browser, or the organisation's database. */
  projectHome: 'local' | 'cloud';
  /** Draft version the open cloud project was loaded at, for detecting someone else's save. */
  cloudVersion: number;
  doc: ProjectDoc | null;
  selection: Selection;
  past: HistoryEntry[];
  future: HistoryEntry[];
  saveState: SaveState;
  view: {
    mode: ViewMode;
    zones: Record<ZoneNumber, boolean>;
    labels: boolean;
    /** The origin marker and the X/Y/Z axes. */
    datum: boolean;
    dockTab: DockTab;
    dockOpen: boolean;
  };
  /** Bumped to ask the viewport to frame the selection (or everything when nothing is selected). */
  focusRequest: number;
  toast: { id: number; text: string } | null;
  /** The document as it was when the current drag began. */
  dragBase: ProjectDoc | null;
  screen: 'workspace' | 'dossier';
  /** Shows the sign-in screen over everything else. */
  signingIn: boolean;
  setSigningIn: (v: boolean) => void;
  paletteOpen: boolean;
  setPalette: (open: boolean) => void;
  /** The gesture sheet. */
  helpOpen: boolean;
  setHelp: (open: boolean) => void;
  /** Bulk entry from a pasted table. */
  bulkOpen: boolean;
  setBulk: (open: boolean) => void;
  setScreen: (s: 'workspace' | 'dossier') => void;

  openProject: (id: string, doc: ProjectDoc, home?: 'local' | 'cloud', version?: number) => void;
  setCloudVersion: (v: number) => void;
  closeProject: () => void;
  edit: (label: string, recipe: (draft: ProjectDoc) => void) => void;
  /**
   * A drag shows its result as it happens but must leave one undo step behind. dragStart remembers
   * the document, dragPreview re-applies to that remembered copy without touching history, and
   * dragCommit puts the document back and makes the single real edit.
   */
  dragStart: () => void;
  dragPreview: (recipe: (draft: ProjectDoc) => void) => void;
  dragCommit: (label: string, recipe: (draft: ProjectDoc) => void) => void;
  dragCancel: () => void;
  /** Shown while dragging, e.g. "Pump house · Length 6.0 m". */
  dragReadout: string | null;
  undo: () => void;
  redo: () => void;
  select: (selection: Selection) => void;
  setView: (view: Partial<State['view']>) => void;
  requestFocus: () => void;
  setSaveState: (s: SaveState) => void;
  notify: (text: string) => void;
}

const HISTORY_LIMIT = 200;

export const useStore = create<State>()((set, get) => ({
  projectId: null,
  projectHome: 'local',
  cloudVersion: 0,
  doc: null,
  selection: null,
  past: [],
  future: [],
  saveState: 'saved',
  view: { mode: 'persp', zones: { 0: true, 1: true, 2: true }, labels: true, datum: true, dockTab: 'schedule', dockOpen: true },
  focusRequest: 0,
  toast: null,
  dragBase: null,
  dragReadout: null,
  screen: 'workspace',
  signingIn: false,
  setSigningIn: (signingIn) => set({ signingIn }),
  paletteOpen: false,
  setPalette: (paletteOpen) => set({ paletteOpen }),
  helpOpen: false,
  setHelp: (helpOpen) => set({ helpOpen }),
  bulkOpen: false,
  setBulk: (bulkOpen) => set({ bulkOpen }),
  setScreen: (screen) => set({ screen }),

  openProject: (id, doc, home = 'local', version = 0) => set({ projectId: id, doc, projectHome: home, cloudVersion: version, selection: null, past: [], future: [], saveState: 'saved', focusRequest: get().focusRequest + 1 }),
  setCloudVersion: (cloudVersion) => set({ cloudVersion }),
  closeProject: () => set({ projectId: null, doc: null, projectHome: 'local', cloudVersion: 0, selection: null, past: [], future: [], screen: 'workspace' }),

  dragStart: () => set({ dragBase: get().doc }),
  dragPreview: (recipe) => {
    const base = get().dragBase;
    if (!base) return;
    set({ doc: produce(base, recipe) });
  },
  dragCommit: (label, recipe) => {
    const base = get().dragBase;
    if (!base) return;
    set({ doc: base, dragBase: null, dragReadout: null });
    get().edit(label, recipe);
  },
  dragCancel: () => {
    const base = get().dragBase;
    set({ dragBase: null, dragReadout: null, ...(base ? { doc: base } : {}) });
  },
  edit: (label, recipe) => {
    const doc = get().doc;
    if (!doc) return;
    const [next, patches, inverse] = produceWithPatches(doc, recipe);
    if (!patches.length) return;
    set({ doc: next, past: [...get().past, { label, patches, inverse }].slice(-HISTORY_LIMIT), future: [] });
  },
  undo: () => {
    const { past, doc } = get();
    const entry = past[past.length - 1];
    if (!entry || !doc) return;
    set({ doc: applyPatches(doc, entry.inverse), past: past.slice(0, -1), future: [entry, ...get().future] });
    get().notify(`Undid: ${entry.label}`);
  },
  redo: () => {
    const { future, doc } = get();
    const entry = future[0];
    if (!entry || !doc) return;
    set({ doc: applyPatches(doc, entry.patches), future: future.slice(1), past: [...get().past, entry] });
    get().notify(`Redid: ${entry.label}`);
  },
  select: (selection) => set({ selection }),
  setView: (view) => set({ view: { ...get().view, ...view } }),
  requestFocus: () => set({ focusRequest: get().focusRequest + 1 }),
  setSaveState: (saveState) => set({ saveState }),
  notify: (text) => set({ toast: { id: Date.now(), text } }),
}));
